import React, { FC, useCallback, useState } from 'react';
import {
  deleteRewardById,
  getCampNodes,
  getCampRewardList,
  getCouponList,
  sortRewardList,
} from '@/services/ConsoleRequest';
import { CSS } from '@dnd-kit/utilities';
import { useParams, useRequest } from 'umi';
import CreateRewardModal from '@/pages/backend/cardCampSettings/rewardSettings/createRewardModal';
import { Button, Image, Modal, Space } from 'antd';
import { DeleteOutlined, EditOutlined, MenuOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { TConsoleReward } from '@/types/console/reward';
import {
  sourceData,
  TRewardConditionRowType,
} from '@/pages/backend/cardCampSettings/rewardSettings/rewardCondition';
import { useSortable } from '@dnd-kit/sortable';
import DragList from '@/pages/backend/cardCampSettings/nodeSettings/DragList';
import { get } from 'lodash';

export function RewardSortableItem(props: any) {
  const id = props['data-row-key'];
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
  });

  const { children } = props;

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };
  return (
    <tr {...props} ref={setNodeRef} style={style}>
      {React.Children.map(children, (child) => {
        if ((child as React.ReactElement).key === 'sort') {
          return React.cloneElement(child as React.ReactElement, {
            children: (
              <div
                ref={setActivatorNodeRef}
                style={{
                  touchAction: 'none',
                  cursor: 'move',
                  width: '100%',
                  height: '100%',
                }}
                {...attributes}
                {...listeners}
              >
                <MenuOutlined />
              </div>
            ),
          });
        }

        return child;
      })}
    </tr>
  );
}

const RewardSettings: FC = () => {
  const [activeRewardInfo, setActiveRewardInfo] = useState<TConsoleReward | undefined>();
  const [visible, setVisible] = useState(false);
  const { id: campId } = useParams<{ id: string }>();
  const [dataSource, setDataSource] = useState<TConsoleReward[]>([]);
  const { loading, run } = useRequest(
    () => {
      return getCampRewardList(campId);
    },
    {
      onSuccess: (res) => {
        if (Array.isArray(res)) {
          setDataSource(res);
        } else {
          setDataSource([]);
        }
      },
    },
  );
  const { data: couponList } = useRequest(() => {
    return getCouponList();
  });
  const { data: nodeList } = useRequest(() => {
    return getCampNodes(campId);
  });
  const createConditionText = useCallback(
    (condition) => {
      let array = [];
      for (const field in sourceData) {
        if (condition[field]) {
          if (field === 'selectedEarnCredits' && condition.earnCredits) {
            array.push(`[${sourceData[field].label}≥${condition.earnCredits}]`);
          } else if (field === 'selectedFinishNodes' && Array.isArray(condition.finishNodes)) {
            if (Array.isArray(nodeList)) {
              // 全部选中了
              if (condition.finishNodes.length === nodeList.length) {
                array.push('[完成所有打卡节点]');
              } else {
                const nameArray = [];
                for (const node of condition.finishNodes) {
                  const findItem = nodeList.find((nodeItem) => nodeItem.id === node);
                  if (findItem) {
                    nameArray.push(findItem.name);
                  }
                }
                array.push(`[完成：${nameArray.join('、')}]`);
              }
            }
          } else if (
            field === 'selectedRank' &&
            condition.minRankAfterFinishCamp !== undefined &&
            condition.maxRankAfterFinishCamp !== undefined
          ) {
            array.push(
              `[排名${condition.minRankAfterFinishCamp}~${condition.maxRankAfterFinishCamp}]`,
            );
          } else {
            array.push(`[${sourceData[field as TRewardConditionRowType].label}]`);
          }
        }
      }
      return array.join('且');
    },
    [sourceData, nodeList],
  );

  return (
    <div style={{ padding: 24 }}>
      <Button
        icon={<PlusSquareOutlined />}
        type="primary"
        onClick={() => {
          setVisible(true);
          setActiveRewardInfo(undefined);
        }}
      >
        新建奖励
      </Button>
      <DragList
        onDragEnd={(data) => {
          sortRewardList(data.map((item, index) => ({ id: item.id, index })));
        }}
        data={dataSource}
        tableProps={{
          columns: [
            {
              title: ' ',
              key: 'sort',
              dataIndex: 'sort',
              width: 30,
              render: () => <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />,
            },
            {
              width: 60,
              dataIndex: 'picture',
              title: '图片',
              key: 'picture',
              render: (value) => {
                if (!value) {
                  return '暂无';
                }
                return <Image src={value} width={40} height={40} />;
              },
            },
            {
              dataIndex: 'name',
              title: '奖励名称',
              key: 'name',
              render: (value) => {
                return <div className="truncate max-w-[300px]">{value}</div>;
              },
            },
            {
              dataIndex: 'condition',
              title: '兑换条件',
              width: 350,
              key: 'condition',
              render: (value) => {
                return <div className="truncate max-w-[300px]">{createConditionText(value)}</div>;
              },
            },
            {
              dataIndex: 'content',
              title: '奖励内容',
              key: 'content',
              render: (value, record) => {
                if (value === 0) {
                  return 'F币';
                }
                if (value === 2) {
                  return '自定义';
                }
                if (Array.isArray(couponList)) {
                  const couponType = record.contentInfo?.type;
                  const findItem = couponList.find(
                    (item) => Number(item.couponId) === Number(couponType),
                  );
                  if (findItem) {
                    return findItem.couponName;
                  }
                }
                return '--';
              },
            },
            {
              dataIndex: 'money',
              title: '金额',
              key: 'money',
              render: (value, record) => {
                return typeof get(record, 'contentInfo.amountOfMoney') === 'number'
                  ? get(record, 'contentInfo.amountOfMoney').toFixed(2)
                  : '--';
              },
            },
            {
              dataIndex: 'operation',
              title: '操作',
              key: 'operation',
              render: (_, record) => {
                return (
                  <Space>
                    <EditOutlined
                      className="cursor-pointer hover:text-blue"
                      onClick={() => {
                        setActiveRewardInfo(record);
                        setVisible(true);
                      }}
                    />
                    <DeleteOutlined
                      className="cursor-pointer hover:text-red"
                      onClick={() => {
                        Modal.confirm({
                          okType: 'danger',
                          title: '确认删除该奖励？',
                          content: '用户已经领取的奖励不会收回',
                          okText: '删除',
                          onOk: async () => {
                            await deleteRewardById(record.id);
                            await run();
                          },
                        });
                      }}
                    />
                  </Space>
                );
              },
            },
          ],
        }}
      />

      {visible && (
        <CreateRewardModal
          index={dataSource.length}
          couponList={couponList}
          activeRewardInfo={activeRewardInfo}
          visible={visible}
          onOk={async () => {
            await run();
            setVisible(false);
            setActiveRewardInfo(undefined);
          }}
          onClose={() => {
            setVisible(false);
            setActiveRewardInfo(undefined);
          }}
        />
      )}
    </div>
  );
};
export default RewardSettings;
